import React, { useState, useEffect } from 'react';
import { Edit3, Trash2, Eye, Pin, Globe, Plus, Search, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import AdminPostCreator from '../../components/admin/AdminPostCreator';

interface AdminPost {
  id: string;
  content: string;
  sport: string;
  isAdminPost: boolean;
  pinned: boolean;
  pinnedAt: string | null;
  likes: number;
  comments: number;
  shares: number;
  timeAgo: string;
  createdAt: string;
}

const AdminPostsPage: React.FC = () => {
  const [posts, setPosts] = useState<AdminPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sportFilter, setSportFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPost, setEditingPost] = useState<AdminPost | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const sportOptions = [
    'General', 'Football', 'Cricket', 'Tennis', 'Chess', 'Basketball', 
    'Swimming', 'Badminton', 'Athletics', 'Volleyball', 'Hockey', 
    'Boxing', 'Wrestling', 'Gymnastics', 'Cycling'
  ];

  useEffect(() => {
    fetchAdminPosts();
  }, [sportFilter]);

  const fetchAdminPosts = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      if (sportFilter !== 'all') params.append('sport', sportFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await api.get(`/posts/admin/my-posts?${params.toString()}`);
      
      if (response.data.success) {
        setPosts(response.data.data.posts || []);
      }
    } catch (error) {
      console.error('Error fetching admin posts:', error);
      toast.error('Failed to load admin posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchAdminPosts();
  };

  const handleEditPost = (post: AdminPost) => {
    setEditingPost(post);
    setShowEditModal(true);
  };

  const handleUpdatePost = async (postId: string, content: string, sport: string, priority: string) => {
    try {
      const response = await api.put(`/posts/${postId}`, {
        content,
        sport,
        pinned: priority === 'high'
      });

      if (response.data.success) {
        toast.success('Post updated successfully');
        fetchAdminPosts();
        setShowEditModal(false);
        setEditingPost(null);
      }
    } catch (error: any) {
      console.error('Error updating post:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to update post');
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this admin post? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await api.delete(`/posts/${postId}`);
      
      if (response.data.success) {
        toast.success('Post deleted successfully');
        fetchAdminPosts();
      }
    } catch (error: any) {
      console.error('Error deleting post:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to delete post');
    }
  };

  const handleTogglePin = async (postId: string, currentPinned: boolean) => {
    try {
      const response = await api.put(`/posts/${postId}`, {
        pinned: !currentPinned
      });

      if (response.data.success) {
        toast.success(currentPinned ? 'Post unpinned' : 'Post pinned to top');
        fetchAdminPosts();
      }
    } catch (error: any) {
      console.error('Error toggling pin:', error);
      toast.error('Failed to update post');
    }
  };

  const filteredPosts = posts.filter(post => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return post.content.toLowerCase().includes(query);
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPinnedTime = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Admin Posts</h1>
            <p className="text-gray-600">Manage your official announcements and posts</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Create New Post</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search your posts..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </form>

          {/* Sport Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={sportFilter}
              onChange={(e) => setSportFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Sports</option>
              {sportOptions.map((sport) => (
                <option key={sport} value={sport}>{sport}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Posts List */}
      <div className="p-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-12">
            <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No admin posts found</h3>
            <p className="text-gray-600 mb-4">Create your first admin post to communicate with all users.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Admin Post
            </button>
          </div>
        ) : (
          <div className="grid gap-6">
            {filteredPosts.map((post) => (
              <div key={post.id} className={`bg-white rounded-lg shadow-sm border ${post.pinned ? 'border-red-200 ring-2 ring-red-500 ring-opacity-20' : 'border-gray-200'}`}>
                {/* Post Header */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {post.pinned && (
                        <span className="flex items-center space-x-1 px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                          <Pin className="w-3 h-3" />
                          <span>PINNED</span>
                          {post.pinnedAt && (
                            <span className="ml-1 text-red-700" title={formatDate(post.pinnedAt)}>
                              • {formatPinnedTime(post.pinnedAt)}
                            </span>
                          )}
                        </span>
                      )}
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-bold rounded-full">
                        ADMIN POST
                      </span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                        {post.sport}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleTogglePin(post.id, post.pinned)}
                        className={`p-2 rounded-lg transition-colors ${
                          post.pinned 
                            ? 'text-red-600 bg-red-50 hover:bg-red-100' 
                            : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                        }`}
                        title={post.pinned ? 'Unpin post' : 'Pin to top'}
                      >
                        <Pin className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditPost(post)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit post"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeletePost(post.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete post"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Post Content */}
                <div className="p-4">
                  <p className="text-gray-900 whitespace-pre-wrap">{post.content}</p>
                </div>

                {/* Post Stats */}
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex space-x-4">
                      <span>{post.likes} likes</span>
                      <span>{post.comments} comments</span>
                      <span>{post.shares} shares</span>
                    </div>
                    <span>{formatDate(post.createdAt)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Post Modal */}
      <AdminPostCreator
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onPostCreated={() => {
          fetchAdminPosts();
          setShowCreateModal(false);
        }}
      />

      {/* Edit Post Modal */}
      {editingPost && (
        <EditPostModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingPost(null);
          }}
          post={editingPost}
          onUpdate={handleUpdatePost}
        />
      )}
    </div>
  );
};

// Edit Post Modal Component
interface EditPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: AdminPost;
  onUpdate: (postId: string, content: string, sport: string, priority: string) => void;
}

const EditPostModal: React.FC<EditPostModalProps> = ({ isOpen, onClose, post, onUpdate }) => {
  const [content, setContent] = useState(post.content);
  const [sport, setSport] = useState(post.sport);
  const [priority, setPriority] = useState(post.pinned ? 'high' : 'normal');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sportOptions = [
    'General', 'Football', 'Cricket', 'Tennis', 'Chess', 'Basketball', 
    'Swimming', 'Badminton', 'Athletics', 'Volleyball', 'Hockey', 
    'Boxing', 'Wrestling', 'Gymnastics', 'Cycling'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!content.trim()) {
      toast.error('Please enter post content');
      return;
    }

    setIsSubmitting(true);
    try {
      await onUpdate(post.id, content.trim(), sport, priority);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Edit Admin Post</h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Post Content *
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full h-32 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              maxLength={2000}
              required
            />
            <div className="text-xs text-gray-500 text-right">
              {content.length}/2000
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Sport Category
            </label>
            <select
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {sportOptions.map((sportOption) => (
                <option key={sportOption} value={sportOption}>
                  {sportOption}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Post Priority
            </label>
            <div className="space-y-2">
              <label className="flex items-center space-x-3">
                <input
                  type="radio"
                  name="priority"
                  value="normal"
                  checked={priority === 'normal'}
                  onChange={(e) => setPriority(e.target.value)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span>Normal - Regular admin post</span>
              </label>
              <label className="flex items-center space-x-3">
                <input
                  type="radio"
                  name="priority"
                  value="high"
                  checked={priority === 'high'}
                  onChange={(e) => setPriority(e.target.value)}
                  className="text-red-600 focus:ring-red-500"
                />
                <span>High Priority - Pin to top of feeds</span>
              </label>
            </div>
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!content.trim() || isSubmitting}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Updating...' : 'Update Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminPostsPage;